"use client";
import React, { useState } from "react";
import { useAuthService } from "@/hooks/services";
import { authRoutes } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { toast } from "@/hooks/use-toast";
import { Spinner } from "@/components/ui/spinner";
import { useTranslations } from "next-intl";

type ForgotPasswordFormProps = {
  openLoginModal?: () => void;
  isModal?: boolean; // Add isModal prop for consistent styling
};

export function ForgotPasswordForm({
  openLoginModal,
  isModal = false,
}: ForgotPasswordFormProps) {
  const t = useTranslations("ForgotPassword");
  const { requestPasswordRecovery } = useAuthService();
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const router = useRouter();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      setError(t("emailRequired"));
      toast({
        title: t("errorTitle"),
        description: t("emailRequired"),
        variant: "destructive",
      });
      return;
    }
    setIsLoading(true);
    setError(null);

    try {
      const success = await requestPasswordRecovery(email);
      setIsEmailSent(true);
      if (success) {
        toast({
          title: t("emailSentTitle"),
          description: `${t("emailSentInfo1")} ${email}, ${t("emailSentInfo2")}`,
        });
      }
    } catch (err: unknown) {
      // Don't expose whether the email exists for security reasons
      // Still set success to true even on error
      setIsEmailSent(true);
      console.error("Password recovery request error:", err);
      // Just log the error but don't display it to user
    } finally {
      setIsLoading(false);
    }
  };

  if (isEmailSent) {
    return (
      <div
        className={`flex h-auto w-full flex-col items-center justify-center ${isModal ? "p-0" : "px-4 py-6 sm:px-6 sm:py-8 md:py-12"}`}
      >
        <Card
          className={`w-full ${isModal ? "max-w-full flex-shrink-0 rounded-none border-0 shadow-none" : "mx-auto max-w-[95%] shadow-lg sm:max-w-[420px] md:max-w-md"}`}
        >
          <CardHeader
            className={`space-y-1 text-center ${isModal ? "p-3 sm:p-4" : "p-4 sm:p-6"}`}
          >
            <CardTitle
              className={`${isModal ? "text-lg sm:text-xl" : "text-xl sm:text-2xl"} font-bold`}
            >
              {t("emailSentTitle")}
            </CardTitle>
            <CardDescription className="text-sm sm:text-base">
              {t("emailSentDescription")}
            </CardDescription>
          </CardHeader>
          <CardContent
            className={`grid gap-4 ${isModal ? "px-3 py-2 sm:px-4 sm:py-3" : "p-4 sm:p-6"}`}
          >
            <p className="text-sm text-muted-foreground">
              {t("emailSentInfo1")} <strong>{email}</strong>,{" "}
              {t("emailSentInfo2")}
            </p>
            <p className="text-sm text-muted-foreground">
              {t("emailSentCheckFolders")}
            </p>
          </CardContent>
          <CardFooter
            className={`flex flex-col gap-4 ${isModal ? "px-3 py-2 sm:px-4 sm:py-3" : "p-4 sm:p-6"}`}
          >
            <Button
              variant="outline"
              onClick={() => {
                if (openLoginModal) {
                  openLoginModal();
                } else {
                  router.push("/?showLogin=true");
                }
              }}
              className="w-full"
            >
              {t("returnToLoginButton")}
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div
      className={`flex h-auto w-full flex-col items-center justify-center ${isModal ? "p-0" : "px-4 py-6 sm:px-6 sm:py-8 md:py-12"}`}
    >
      <Card
        className={`w-full ${isModal ? "max-w-full flex-shrink-0 rounded-none border-0 shadow-none" : "mx-auto max-w-[95%] shadow-lg sm:max-w-[420px] md:max-w-md"}`}
      >
        <CardHeader
          className={`space-y-1 text-center ${isModal ? "p-3 sm:p-4" : "p-4 sm:p-6"}`}
        >
          <CardTitle
            className={`${isModal ? "text-lg sm:text-xl" : "text-xl sm:text-2xl"} font-bold`}
          >
            {t("formTitle")}
          </CardTitle>
          <CardDescription className="text-sm sm:text-base">
            {t("formDescription")}
          </CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent
            className={`grid gap-4 ${isModal ? "px-3 py-2 sm:px-4 sm:py-3" : "p-4 sm:p-6"}`}
          >
            <div className="grid gap-2">
              <Label htmlFor="email">{t("emailLabel")}</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={e => setEmail(e.target.value)}
                disabled={isLoading}
                className="h-9 sm:h-10"
              />
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
          </CardContent>
          <CardFooter
            className={`flex flex-col gap-4 ${isModal ? "px-3 py-2 sm:px-4 sm:py-3" : "p-4 sm:p-6"}`}
          >
            <Button
              type="submit"
              className="h-9 w-full sm:h-10"
              disabled={isLoading}
            >
              {isLoading ? (
                <Spinner size="sm" className="mr-2" />
              ) : (
                t("sendResetLinkButton")
              )}
            </Button>
            <div className="text-center text-sm text-muted-foreground">
              {openLoginModal ? (
                <button
                  type="button"
                  onClick={openLoginModal}
                  className="underline hover:text-primary"
                >
                  {t("backToLoginLink")}
                </button>
              ) : (
                <Link
                  href="/?showLogin=true"
                  passHref
                  className="underline hover:text-primary"
                >
                  {t("backToLoginLink")}
                </Link>
              )}
            </div>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}

export function ResetPasswordForm() {
  const t = useTranslations("ResetPassword");
  const { resetPassword } = useAuthService();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const router = useRouter();
  const searchParams = useSearchParams();
  const token = searchParams.get("token");

  React.useEffect(() => {
    if (!token) {
      setError(t("invalidResetToken"));
      toast({
        title: t("errorTitle"),
        description: t("invalidResetToken"),
        variant: "destructive",
      });
    }
  }, [token, t]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) {
      setError(t("resetTokenMissing"));
      toast({
        title: t("errorTitle"),
        description: t("resetTokenMissing"),
        variant: "destructive",
      });
      return;
    }
    if (password !== confirmPassword) {
      setError(t("passwordsDoNotMatch"));
      toast({
        title: t("errorTitle"),
        description: t("passwordsDoNotMatch"),
        variant: "destructive",
      });
      return;
    }
    setIsLoading(true);
    setError(null);
    setMessage(null);

    try {
      const success = await resetPassword({
        token,
        new_password: password,
      });
      if (success) {
        setMessage(t("resetSuccessMessage"));
        toast({
          title: t("resetSuccessTitle"),
          description: t("resetSuccessMessage"),
        });
        router.push(authRoutes.MODAL_LOGIN);
      } else {
        setError(t("resetFailedMessage"));
      }
    } catch (err: unknown) {
      const apiError = err as {
        response?: { data?: { detail?: string | string[] } };
      };
      const errorMessage =
        typeof apiError.response?.data?.detail === "string"
          ? apiError.response.data.detail
          : t("resetFailedMessage");
      setError(errorMessage);
      toast({
        title: t("errorTitle"),
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card className="w-full bg-card">
      <CardHeader className="space-y-1">
        <CardTitle className="text-2xl">{t("resetFormTitle")}</CardTitle>
        <CardDescription>{t("resetFormDescription")}</CardDescription>
      </CardHeader>
      {token ? (
        <form onSubmit={handleSubmit}>
          <CardContent className="grid gap-4">
            <div className="grid gap-2">
              <Label htmlFor="password">{t("newPasswordLabel")}</Label>
              <PasswordInput
                id="password"
                autoCompleteType="new-password"
                required
                minLength={8}
                value={password}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setPassword(e.target.value)
                }
                disabled={isLoading}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="confirmPassword">
                {t("confirmPasswordLabel")}
              </Label>
              <PasswordInput
                id="confirmPassword"
                autoCompleteType="new-password"
                required
                minLength={8}
                value={confirmPassword}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setConfirmPassword(e.target.value)
                }
                disabled={isLoading}
              />
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
            {message && <p className="text-sm text-success">{message}</p>}
          </CardContent>
          <CardFooter className="flex flex-col gap-4">
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? (
                <Spinner size="sm" className="mr-2" />
              ) : (
                t("resetPasswordButton")
              )}
            </Button>
          </CardFooter>
        </form>
      ) : (
        <CardContent>
          <p className="text-sm text-destructive">
            {error || t("noTokenProvided")}
          </p>
          <Link
            href={authRoutes.FORGOT_PASSWORD}
            passHref
            className="mt-4 inline-block text-sm text-primary underline hover:text-primary/80"
          >
            {t("requestNewLinkText")}
          </Link>
        </CardContent>
      )}
    </Card>
  );
}
